import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Car, UserCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = ["CA", "AZ", "OR", "WA", "NV", "UT", "TX", "NM"];

const AssignmentDashboard = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("unassigned"); // "unassigned", "assigned", or "byInstaller"
  const [selectedState, setSelectedState] = useState("all"); // State filter

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [jobsResponse, installersResponse] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/installers`)
      ]);
      
      setJobs(jobsResponse.data);
      setInstallers(installersResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignJob = async (jobId, installerId, installerName) => {
    try {
      await axios.patch(`${API}/bookings/${jobId}/assign?installer_id=${installerId}&installer_name=${encodeURIComponent(installerName)}`);
      toast.success(`Job assigned to ${installerName}`);
      fetchData();
    } catch (error) {
      console.error("Error assigning job:", error);
      toast.error("Failed to assign job");
    }
  };

  const handleUnassignJob = async (jobId) => {
    try {
      await axios.patch(`${API}/bookings/${jobId}/unassign`);
      toast.success("Job unassigned");
      fetchData();
    } catch (error) {
      console.error("Error unassigning job:", error);
      toast.error("Failed to unassign job");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Filter jobs by state
  const filteredJobs = selectedState === "all" 
    ? jobs 
    : jobs.filter(job => job.state === selectedState);

  const unassignedJobs = filteredJobs.filter(job => !job.assigned_installer_id);
  const assignedJobs = filteredJobs.filter(job => job.assigned_installer_id);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/h2g")}
                variant="ghost"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
              <div className="border-l border-gray-300 h-6"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Job Assignment Dashboard
                </h1>
                <p className="text-sm text-gray-600">
                  Assign jobs to installers
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/installers")}
              variant="outline"
            >
              Manage Installers
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-600">Total Jobs {selectedState !== "all" && `(${selectedState})`}</div>
            <div className="text-3xl font-bold text-gray-900 mt-2">{filteredJobs.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-6">
            <div className="text-sm font-medium text-orange-600">Unassigned</div>
            <div className="text-3xl font-bold text-orange-600 mt-2">{unassignedJobs.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
            <div className="text-sm font-medium text-green-600">Assigned</div>
            <div className="text-3xl font-bold text-green-600 mt-2">{assignedJobs.length}</div>
          </div>
        </div>

        {/* State Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-700">Filter by State:</label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedState("all")}
                className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                  selectedState === "all"
                    ? "bg-blue-50 border-blue-500 text-blue-700"
                    : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                }`}
              >
                All States
              </button>
              {STATES.map(state => (
                <button
                  key={state}
                  onClick={() => setSelectedState(state)}
                  className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                    selectedState === state
                      ? "bg-blue-50 border-blue-500 text-blue-700"
                      : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                  }`}
                >
                  {state}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab("unassigned")}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === "unassigned"
                  ? "bg-orange-50 text-orange-700 border-b-2 border-orange-500"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Unassigned Jobs ({unassignedJobs.length})
            </button>
            <button
              onClick={() => setActiveTab("assigned")}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === "assigned"
                  ? "bg-green-50 text-green-700 border-b-2 border-green-500"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Assigned Jobs ({assignedJobs.length})
            </button>
            <button
              onClick={() => setActiveTab("byInstaller")}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === "byInstaller"
                  ? "bg-blue-50 text-blue-700 border-b-2 border-blue-500"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              By Installer
            </button>
          </div>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 mt-2">Loading jobs...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeTab === "unassigned" ? (
              unassignedJobs.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <UserCheck className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-500">All jobs have been assigned!</p>
                </div>
              ) : (
                unassignedJobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    installers={installers}
                    onAssign={handleAssignJob}
                    formatDate={formatDate}
                  />
                ))
              )
            ) : activeTab === "assigned" ? (
              assignedJobs.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No jobs assigned yet</p>
                </div>
              ) : (
                assignedJobs.map((job) => (
                  <AssignedJobCard
                    key={job.id}
                    job={job}
                    onUnassign={handleUnassignJob}
                    formatDate={formatDate}
                  />
                ))
              )
            ) : (
              <InstallerGroupView
                installers={installers}
                assignedJobs={assignedJobs}
                formatDate={formatDate}
                onUnassign={handleUnassignJob}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Job Card Component for Unassigned Jobs
const JobCard = ({ job, installers, onAssign, formatDate }) => {
  const [selectedInstaller, setSelectedInstaller] = useState("");

  const handleAssign = () => {
    if (!selectedInstaller) {
      toast.error("Please select an installer");
      return;
    }

    const installer = installers.find(i => i.id === selectedInstaller);
    onAssign(job.id, installer.id, installer.name);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {job.name} {job.last_name}
          </h3>
          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(job.install_date)}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {job.state}
            </span>
          </div>
        </div>
        <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          Unassigned
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Phone:</span> {job.phone}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Email:</span> {job.email}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Address:</span> {job.address}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">City:</span> {job.city}, {job.state} {job.zip}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Lead:</span> {job.lead} | <span className="font-medium">Source:</span> {job.source}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-gray-700 mb-1">
            <Car className="w-4 h-4" />
            <span>{job.vehicle_year} {job.vehicle_make} {job.vehicle_model}</span>
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Service:</span> {job.service}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Type:</span> {job.refurb_or_new}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Discount:</span> ${job.discount}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Phone Price:</span> ${job.phone_price || '0'}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Pay Plan:</span> {job.pay_plan}
          </div>
          {job.notes && (
            <div className="text-gray-700 mt-2 pt-2 border-t border-gray-200">
              <span className="font-medium">Notes:</span> {job.notes}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 items-center pt-4 border-t border-gray-200">
        <select
          value={selectedInstaller}
          onChange={(e) => setSelectedInstaller(e.target.value)}
          className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
        >
          <option value="">Select Installer...</option>
          {installers.map((installer) => (
            <option key={installer.id} value={installer.id}>
              {installer.name}
            </option>
          ))}
        </select>
        <button
          onClick={handleAssign}
          className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <UserCheck className="w-4 h-4" />
          Assign
        </button>
      </div>
    </div>
  );
};

// Assigned Job Card Component
const AssignedJobCard = ({ job, onUnassign, formatDate }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {job.name} {job.last_name}
          </h3>
          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(job.install_date)}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {job.state}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            Assigned to {job.assigned_installer_name}
          </span>
          <button
            onClick={() => onUnassign(job.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Unassign"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Phone:</span> {job.phone}
          </div>
          <div className="text-gray-700 mb-1">
            <span className="font-medium">Address:</span> {job.address}, {job.city}, {job.state}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-gray-700 mb-1">
            <Car className="w-4 h-4" />
            <span>{job.vehicle_year} {job.vehicle_make} {job.vehicle_model}</span>
          </div>
          <div className="text-gray-700">
            <span className="font-medium">Service:</span> {job.service}
          </div>
        </div>
      </div>
    </div>
  );
};

// Installer Group View Component
const InstallerGroupView = ({ installers, assignedJobs, formatDate, onUnassign }) => {
  const [expandedInstallers, setExpandedInstallers] = useState({});

  const toggleInstaller = (installerId) => {
    setExpandedInstallers(prev => ({
      ...prev,
      [installerId]: !prev[installerId]
    }));
  };

  if (assignedJobs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500">No jobs assigned yet</p>
      </div>
    );
  }

  // Group jobs by installer
  const jobsByInstaller = {};
  assignedJobs.forEach(job => {
    const installerId = job.assigned_installer_id;
    if (!jobsByInstaller[installerId]) {
      jobsByInstaller[installerId] = [];
    }
    jobsByInstaller[installerId].push(job);
  });

  return (
    <div className="space-y-4">
      {installers.map(installer => {
        const installerJobs = jobsByInstaller[installer.id] || [];
        
        if (installerJobs.length === 0) return null;

        const isExpanded = expandedInstallers[installer.id];

        return (
          <div key={installer.id} className="bg-white rounded-lg shadow-sm border-2 border-blue-200 overflow-hidden">
            {/* Installer Header - Always Visible, Clickable */}
            <div 
              className="flex items-center justify-between p-6 cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => toggleInstaller(installer.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{installer.name}</h2>
                  <p className="text-sm text-gray-600">{installerJobs.length} job{installerJobs.length !== 1 ? 's' : ''} assigned</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={async (e) => {
                    e.stopPropagation(); // Prevent toggle when clicking button
                    try {
                      await axios.patch(`${API}/installer/${installer.id}/send-list`);
                      const link = `${window.location.origin}/installer/${installer.link_slug}`;
                      
                      // Try clipboard API to copy link
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(link);
                        toast.success(`Job list sent to ${installer.name}! Link copied to clipboard.`);
                      } else {
                        // Fallback
                        prompt('Copy this link to send to installer:', link);
                        toast.success(`Job list marked as sent to ${installer.name}!`);
                      }
                      
                      // Refresh data to show updated status
                      window.location.reload();
                    } catch (error) {
                      console.error('Error sending job list:', error);
                      toast.error('Failed to send job list');
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-medium"
                >
                  <MapPin className="w-4 h-4" />
                  Send Job List
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent toggle when clicking button
                    const link = `/installer/${installer.link_slug}`;
                    window.open(link, '_blank');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                  title="Open installer view in new tab"
                >
                  👁️ View as Installer
                </button>
                
                <div className="ml-2 text-gray-400">
                  <svg 
                    className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Jobs List - Collapsible */}
            {isExpanded && (
              <div className="border-t-2 border-blue-100 p-6 bg-gray-50">
                <div className="space-y-3">
              {installerJobs.map(job => {
                const installDate = new Date(job.install_date);
                const contactedDate = job.customer_contacted_at ? new Date(job.customer_contacted_at) : null;
                const hoursUntilInstall = contactedDate ? (installDate - contactedDate) / (1000 * 60 * 60) : null;
                const isCompliant = hoursUntilInstall >= 12;
                
                return (
                  <div key={job.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            {job.name} {job.last_name}
                          </h3>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                            {job.service}
                          </span>
                          {job.list_sent_at && !job.customer_contacted_at && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                              List Sent - Awaiting Contact
                            </span>
                          )}
                          {job.customer_contacted_at && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              isCompliant 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {isCompliant ? '✓ Compliant' : '⚠ Late Contact'}
                            </span>
                          )}
                        </div>
                      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(job.install_date)}
                        </div>
                        <div>
                          <span className="font-medium">Phone:</span> {job.phone}
                        </div>
                        <div>
                          <span className="font-medium">Email:</span> {job.email}
                        </div>
                        <div>
                          <span className="font-medium">Lead:</span> {job.lead} | <span className="font-medium">Source:</span> {job.source}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {job.address}
                        </div>
                        <div>
                          {job.city}, {job.state} {job.zip}
                        </div>
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4" />
                          {job.vehicle_year} {job.vehicle_make} {job.vehicle_model}
                        </div>
                        <div>
                          <span className="font-medium">Type:</span> {job.refurb_or_new}
                        </div>
                        <div>
                          <span className="font-medium">Discount:</span> ${job.discount}
                        </div>
                        <div>
                          <span className="font-medium">Phone Price:</span> ${job.phone_price || '0'}
                        </div>
                        <div>
                          <span className="font-medium">Pay Plan:</span> {job.pay_plan}
                        </div>
                        {job.notes && (
                          <div className="col-span-2 pt-2 border-t border-gray-200">
                            <span className="font-medium">Notes:</span> {job.notes}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onUnassign(job.id)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Unassign"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssignmentDashboard;
